import type { ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client';
import { EMAIL_PREFILL_QUERY_PARAM, LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM } from '@opendoor/auth-fe';
import type { GraphQLFormattedError } from 'graphql';
import { GraphQLError } from 'graphql';
import { merge } from 'lodash-es';

import { globalTrackEvent } from './observability';
import type { TEAM } from './teams';

type Cause = 'user-error' | 'system-error' | 'dev-error';

/**
 * captureErrorStackTrace is a helper function that captures the stack trace of an error.
 * Tracks an event if Error.captureStackTrace is not available in the browser.
 * @param targetObject - The object to capture the stack trace on.
 * @param constructorOpt - The constructor function to exclude from the stack trace.
 */
const captureErrorStackTrace = (
  targetObject: object,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- matching underlying method definition
  constructorOpt: Function,
): void => {
  if (Error.captureStackTrace) {
    Error.captureStackTrace(targetObject, constructorOpt);
  } else {
    // Intentionally not logging to sentry/datadog as an error
    globalTrackEvent('error', 'Error.captureStackTrace is not available in this browser', {
      browser: window.navigator.userAgent,
      error: targetObject,
    });
  }
};

export const toGraphQLError = (error: GraphQLFormattedError) =>
  new GraphQLError(error.message, { extensions: error.extensions, path: error.path });

type AnyGraphQLError = GraphQLError | GraphQLFormattedError;

/**
 * TeamError is a custom error type that includes the team name and source of the error.
 * It is thrown by TeamErrorBoundary when an error occurs in a component tree that is wrapped
 * by an ODTeamBoundary.
 *
 * It includes a `serialize` method that returns the error as a JSON object which can be used in response payloads to help handle server errors on the client (i.e. for deferred loaders).
 *
 * This is the base error type for all errors thrown in Cake
 */
export class TeamError extends Error {
  public name = 'TeamError';
  /**
   * This custom property is used to identify custom error types when deferred loaders return generic error responses.
   * It ensures that the error can be recognized and handled appropriately by the application.
   */
  public __isError = true;
  public cause: Cause;
  public team?: TEAM;
  public message: string;

  constructor(error: Error | string, cause: Cause, team?: TEAM) {
    const message = typeof error === 'string' ? error : error.message;
    super(message, { cause });
    this.team = team;
    this.cause = cause;
    this.message = message;
    captureErrorStackTrace(this, TeamError);
  }

  serialize() {
    return {
      __isError: true,
      name: this.name,
      details: {
        error: this.message,
        cause: this.cause,
        team: this.team,
      },
    };
  }
}

/**
 * UserError is a custom error type that is thrown when a user error occurs.
 */
export class UserError extends TeamError {
  public override name = 'UserError';
  constructor(error: Error | string, team?: TEAM) {
    super(error, 'user-error', team);
    captureErrorStackTrace(this, UserError);
  }
}

/**
 * DevError is a custom error type that is thrown when a developer error occurs.
 */
export class DevError extends TeamError {
  public override name = 'DevError';
  constructor(error: Error | string, team?: TEAM) {
    super(error, 'dev-error', team);
    captureErrorStackTrace(this, DevError);
  }
}

/**
 * SystemError is a custom error type that is thrown when a system error occurs.
 * This is the default error type if the cause is not specified.
 */
export class SystemError extends TeamError {
  public override name = 'SystemError';
  constructor(error: Error | string, team?: TEAM) {
    super(error, 'system-error', team);
    captureErrorStackTrace(this, SystemError);
  }
}

/**
 * UnauthenticatedError is a custom error type that is thrown when a user is not authenticated.
 */
export class UnauthenticatedError extends TeamError {
  public override name = 'UnauthenticatedError';
  public triggeredQuery?: string;
  public emailPrefill: string | null;
  public accessToken: string | null;
  public from: 'apollo-link' | 'data-loader' | 'app-code' | 'unknown';
  public requestUrl: string | undefined;

  constructor(
    triggeredQuery?: string,
    from: 'apollo-link' | 'data-loader' | 'app-code' | 'unknown' = 'unknown',
    team?: TEAM,
    url?: string,
  ) {
    const message = triggeredQuery
      ? 'Attempted to query while unauthenticated'
      : 'User is not authenticated';
    super(message, 'user-error', team);

    // Determine the request URL, falling back to window.location.href if not provided
    const requestUrl = url ? url : window.location.href;
    const searchParams = requestUrl ? new URL(requestUrl).searchParams : new URLSearchParams();
    this.emailPrefill = searchParams.get(EMAIL_PREFILL_QUERY_PARAM);
    this.accessToken = searchParams.get(LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM);
    this.requestUrl = requestUrl;
    this.triggeredQuery = triggeredQuery;
    this.from = from;
    captureErrorStackTrace(this, UnauthenticatedError);
  }

  public override serialize() {
    return merge(super.serialize(), {
      details: {
        triggeredQuery: this.triggeredQuery,
        from: this.from,
        url: this.requestUrl,
      },
    });
  }
}

type AccessErrorType = 'reauthentication-required' | 'permission-denied';

/**
 * UnauthorizedError is a custom error type that is thrown when a user is not authorized to see the experience they're attempting to access.
 */
export class UnauthorizedError extends TeamError {
  public override name = 'UnauthorizedError';
  public triggeredQuery?: string;
  public accessToken: string | null;
  public accessErrorType: AccessErrorType;

  constructor(triggeredQuery?: string, accessErrorType?: AccessErrorType, team?: TEAM) {
    const message = triggeredQuery
      ? 'Attempted to query unauthorized experience'
      : 'User is not authorized to access experience';
    super(message, 'user-error', team);

    const searchParams = new URLSearchParams(window.location.search);
    this.accessToken = searchParams.get(LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM);
    this.triggeredQuery = triggeredQuery;
    this.accessErrorType = accessErrorType ?? 'permission-denied';
    captureErrorStackTrace(this, UnauthorizedError);
  }

  public override serialize() {
    return merge(super.serialize(), {
      details: {
        triggeredQuery: this.triggeredQuery,
        accessErrorType: this.accessErrorType,
      },
    });
  }
}

/**
 * ErrorClassMap is a mapping of error class names to their respective classes.
 * This needs to be updated whenever a new error class is added.
 */
type ErrorClassMap = {
  TeamError: typeof TeamError;
  UserError: typeof UserError;
  DevError: typeof DevError;
  SystemError: typeof SystemError;
  UnauthenticatedError: typeof UnauthenticatedError;
  UnauthorizedError: typeof UnauthorizedError;
};

/**
 * Union type of all error names
 * @internal
 */
export type CakeErrorNames = keyof ErrorClassMap;

/**
 * Type representing an instance of any error class
 * @internal
 */
export type CakeError = InstanceType<ErrorClassMap[CakeErrorNames]>;

// Type representing the serialized response of an error class
type ErrorResponse<K extends CakeErrorNames> = ReturnType<
  ErrorClassMap[K]['prototype']['serialize']
>;

// Union type of all possible error responses
type CakeErrorResponse = ErrorResponse<CakeErrorNames>;

// Type representing the details property of the serialized response of an error class
type ErrorDetails<K extends CakeErrorNames> = ErrorResponse<K>['details'];

// Type guard for UnauthenticatedError
function isUnauthenticatedErrorDetails(
  details: ErrorDetails<CakeErrorNames>,
): details is ErrorDetails<'UnauthenticatedError'> {
  return 'triggeredQuery' in details && 'from' in details && 'team' in details && 'url' in details;
}

// Type guard for UnauthorizedError
function isUnauthorizedErrorDetails(
  details: ErrorDetails<CakeErrorNames>,
): details is ErrorDetails<'UnauthorizedError'> {
  return 'triggeredQuery' in details && 'accessErrorType' in details && 'team' in details;
}

// Mapping of error names to functions that create instances of the corresponding error classes
const ERROR_MAPPING: Record<CakeErrorNames, (details: ErrorDetails<CakeErrorNames>) => CakeError> =
  {
    TeamError: (details) => new TeamError(details.error, details.cause, details.team),
    UserError: (details) => new UserError(details.error, details.team),
    DevError: (details) => new DevError(details.error, details.team),
    SystemError: (details) => new SystemError(details.error, details.team),
    UnauthenticatedError: (genericDetails) => {
      if (isUnauthenticatedErrorDetails(genericDetails)) {
        return new UnauthenticatedError(
          genericDetails.triggeredQuery,
          genericDetails.from,
          genericDetails.team,
          genericDetails.url,
        );
      }
      throw new DevError(
        'Invalid details for UnauthenticatedError: ' + JSON.stringify(genericDetails),
      );
    },
    UnauthorizedError: (genericDetails) => {
      if (isUnauthorizedErrorDetails(genericDetails)) {
        return new UnauthorizedError(
          genericDetails.triggeredQuery,
          genericDetails.accessErrorType,
          genericDetails.team,
        );
      }
      throw new DevError(
        'Invalid details for UnauthorizedError: ' + JSON.stringify(genericDetails),
      );
    },
  };

const GRPC_ERROR_CODE = 'GRPC_ERROR';
const GRPC_PERMISSION_DENIED_EXCEPTION_CODE = 7;
const GRPC_NOT_FOUND_EXCEPTION_CODE = 5;

const INVALID_ARGUMENT_ERROR_CODE = 'INVALID_ARGUMENT';
const NOT_FOUND_ERROR_CODE = 'NOT_FOUND';

type GrpcErrorExtensions = {
  code?: string;
  exception?: { code?: number };
};

/**
 * A type-safe way to retrieve Athena's gRPC error extensions from a GraphQLError
 *
 * @param error a GraphQLError, possibly with gRPC extensions
 * @returns a typed object containing optional error codes
 */
export const getGrpcExtensions = (error?: AnyGraphQLError): GrpcErrorExtensions =>
  error?.extensions ?? {};

const isGrpcNotFoundError = (error: AnyGraphQLError): boolean => {
  const ext = getGrpcExtensions(error);
  return ext.code === GRPC_ERROR_CODE && ext.exception?.code === GRPC_NOT_FOUND_EXCEPTION_CODE;
};

const isGrpcPermissionDeniedError = (error: AnyGraphQLError): boolean => {
  const ext = getGrpcExtensions(error);
  return (
    ext.code === GRPC_ERROR_CODE && ext.exception?.code === GRPC_PERMISSION_DENIED_EXCEPTION_CODE
  );
};

export const isInvalidArgumentError = (error: SystemError | ErrorLike): boolean => {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.some((err) => err.message.includes(INVALID_ARGUMENT_ERROR_CODE));
  }

  return error.message.includes(INVALID_ARGUMENT_ERROR_CODE);
};

export const isNotFoundError = (error: SystemError | ErrorLike): boolean => {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.some((err) => err.message.includes(NOT_FOUND_ERROR_CODE));
  }

  return error.message.includes(NOT_FOUND_ERROR_CODE);
};

/**
 * Check if GraphQLError(s) are gRPC not found errors
 *
 * @param errors one or more GraphQLError
 * @returns true if any errors are gRPC not found
 */
export const hasNotFoundError = (errors: AnyGraphQLError[] | ErrorLike): boolean => {
  if (Array.isArray(errors)) {
    return errors.some(isGrpcNotFoundError);
  }

  if (CombinedGraphQLErrors.is(errors)) {
    return errors.errors.some(isGrpcNotFoundError);
  }

  return false;
};

/**
 * Determines whether the ApolloError includes a gRPC permission error
 */
export const hasPermissionDeniedError = (
  errors: ErrorLike | AnyGraphQLError[] | undefined,
): boolean => {
  if (Array.isArray(errors)) {
    return errors.some(isGrpcPermissionDeniedError);
  }

  if (CombinedGraphQLErrors.is(errors)) {
    return errors.errors.some(isGrpcPermissionDeniedError);
  }

  return false;
};

/**
 * Returns true if a GraphQL error is NotFound (404).
 * TODO(@sellDirect): Update Athena error serialization so we don't need to look at the stacktrace.
 */
export const isGraphQLErrorNotFound = (error: AnyGraphQLError): boolean =>
  error.message.startsWith('HTTP Error 404');

/**
 * Returns true if a GraphQL error is PreconditionFailed (422).
 * TODO(@sellDirect): Update Athena error serialization so we don't need to look at the stacktrace.
 */
export const isGraphQLErrorFailedPrecondition = (error: AnyGraphQLError): boolean =>
  error.message.startsWith('HTTP Error 422');

/**
 * Determines whether auth-gated query requires the customer to re-authenticate.
 * @internal
 */
export const isReAuthenticationRequired = (error: AnyGraphQLError): boolean =>
  error.message.includes(`"code":"reauthentication-required"`);

/**
 * Determines whether the response is a generic error response.
 * @internal
 */
export const isGenericErrorResponse = (
  response: Record<string, unknown>,
): response is CakeErrorResponse =>
  !!response &&
  '__isError' in response &&
  typeof response.__isError === 'boolean' &&
  response.__isError;

function isValidErrorName(name: string): name is CakeErrorNames {
  return name in ERROR_MAPPING;
}

/**
 * Converts a generic error response returned by deferred data loaders to a typed CakeError.
 * @throws SystemError if the error type is unknown.
 * @internal
 */
export function deserializeError(error: Record<string, unknown>): CakeError {
  if (isGenericErrorResponse(error)) {
    if (isValidErrorName(error.name)) {
      const errorConstructor = ERROR_MAPPING[error.name];
      return errorConstructor(error.details);
    }

    // Explicitly track cases where we are unable to deserialize the error
    globalTrackEvent('error', 'Unable to deserialize error', error);
  }
  return new SystemError(JSON.stringify(error));
}
